import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import axios from "axios";
import { EmpresasURL, TiposVehiculoURL } from "../../../API/apiurls";

const buildPreviewRows = (positions) => {
  if (!Array.isArray(positions) || !positions.length) return [];
  const normalized = positions.map((item) => ({
    ...item,
    code: String(item.codigo || "").trim().toUpperCase(),
  }));

  const hasThreeLiteras = normalized.some((item) => item.code === "LITERA_3");
  const hasLiteraAtras = normalized.some((item) => item.code === "LITERA_ATRAS");
  const hasCentro = normalized.some((item) => item.code === "CENTRO");

  if (hasThreeLiteras) {
    return [
      normalized.filter((item) => ["CONDUCTOR", "COPILOTO"].includes(item.code)).map((item) => item.nombre),
      normalized.filter((item) => item.code.startsWith("LITERA_")).map((item) => item.nombre),
    ].filter((row) => row.length);
  }

  if (hasCentro) {
    return [normalized.map((item) => item.nombre)];
  }

  if (hasLiteraAtras) {
    return [
      normalized.filter((item) => ["CONDUCTOR", "COPILOTO"].includes(item.code)).map((item) => item.nombre),
      normalized.filter((item) => item.code === "LITERA_ATRAS").map((item) => item.nombre),
    ].filter((row) => row.length);
  }

  return [normalized.map((item) => item.nombre)];
};

const findTipoId = (vehiculo, tipos) => {
  const modelId = vehiculo?.tipoVehiculoModel?.id;
  if (modelId) return String(modelId);
  const code = String(vehiculo?.tipoVehiculo || "").trim().toUpperCase();
  if (!code) return "";
  const match = (tipos || []).find((item) => String(item.codigo || "").trim().toUpperCase() === code);
  return match ? String(match.id) : "";
};

export function VehiculosModal({ mostrar, cerrar, guardar, editar, datosaeditar, title }) {
  const isEdit = Boolean(datosaeditar?.id);

  const [loading, setLoading] = useState(false);
  const [empresas, setEmpresas] = useState([]);
  const [tiposVehiculo, setTiposVehiculo] = useState([]);
  const [formData, setFormData] = useState({
    id: null,
    placa: "",
    nombre: "",
    tipoVehiculoId: "",
    estado: true,
    empresaId: "",
  });

  useEffect(() => {
    if (!mostrar) return;
    setFormData({
      id: datosaeditar?.id ?? null,
      placa: datosaeditar?.placa ?? "",
      nombre: datosaeditar?.nombre ?? "",
      tipoVehiculoId: "",
      estado: datosaeditar?.estado ?? true,
      empresaId: String(datosaeditar?.empresasModel?.id ?? ""),
    });
  }, [mostrar, datosaeditar]);

  useEffect(() => {
    if (!mostrar) return;

    const loadDeps = async () => {
      try {
        setLoading(true);
        const [empresasRes, tiposRes] = await Promise.all([
          axios.get(EmpresasURL),
          axios.get(TiposVehiculoURL, { params: { activo: true } }),
        ]);
        const empresasList = (empresasRes.data || []).slice().sort((a, b) => Number(a?.id ?? 0) - Number(b?.id ?? 0));
        const tiposList = Array.isArray(tiposRes.data) ? tiposRes.data : [];
        setEmpresas(empresasList);
        setTiposVehiculo(tiposList);

        setFormData((prev) => ({
          ...prev,
          empresaId: prev.empresaId || String(empresasList[0]?.id ?? ""),
          tipoVehiculoId: prev.tipoVehiculoId || findTipoId(datosaeditar, tiposList) || String(tiposList[0]?.id ?? ""),
        }));
      } catch (error) {
        console.error("Error cargando empresas o tipos de vehículo:", error);
        setEmpresas([]);
        setTiposVehiculo([]);
      } finally {
        setLoading(false);
      }
    };

    loadDeps();
  }, [mostrar, datosaeditar]);

  const handleClose = () => cerrar();

  const tipoActual = useMemo(() => {
    return tiposVehiculo.find((item) => String(item.id) === String(formData.tipoVehiculoId)) || tiposVehiculo[0] || null;
  }, [tiposVehiculo, formData.tipoVehiculoId]);

  const previewRows = useMemo(() => buildPreviewRows(tipoActual?.posiciones || []), [tipoActual]);

  const handleSubmit = () => {
    if (!tipoActual) {
      return;
    }

    const payload = {
      ...(datosaeditar || {}),
      placa: formData.placa,
      nombre: formData.nombre,
      tipoVehiculoId: tipoActual.id,
      tipoVehiculo: tipoActual.codigo,
      cantidadCinturones: tipoActual.cantidadCinturones,
      estado: Boolean(formData.estado),
      empresasModel: { id: Number(formData.empresaId) },
    };

    if (isEdit) editar(payload);
    else guardar(payload);

    cerrar();
  };

  return (
    <Modal
      show={mostrar}
      onHide={handleClose}
      centered
      contentClassName="bg-dark text-light border-secondary"
    >
      <Modal.Header closeButton closeVariant="white" className="border-secondary">
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Spinner size="sm" />
            <span>Cargando configuración...</span>
          </div>
        ) : (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Empresa</Form.Label>
              <Form.Select
                value={formData.empresaId}
                onChange={(e) => setFormData((prev) => ({ ...prev, empresaId: e.target.value }))}
                className="bg-dark text-light border-secondary"
              >
                {empresas.map((empresa) => (
                  <option key={empresa.id} value={String(empresa.id)}>
                    {empresa.nombre}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Placa</Form.Label>
              <Form.Control
                type="text"
                value={formData.placa}
                onChange={(e) => setFormData((prev) => ({ ...prev, placa: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Nombre del vehículo</Form.Label>
              <Form.Control
                type="text"
                value={formData.nombre}
                onChange={(e) => setFormData((prev) => ({ ...prev, nombre: e.target.value }))}
              />
            </Form.Group>

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Form.Group style={{ flex: "1 1 100%" }}>
                <Form.Label>Tipo de vehículo</Form.Label>
                <div
                  style={{
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 12,
                    padding: 14,
                    background: "rgba(255,255,255,0.03)",
                  }}
                >
                  <div style={{ display: "grid", gap: 10, marginBottom: 14 }}>
                    {tiposVehiculo.map((tipo) => {
                      const active = String(formData.tipoVehiculoId) === String(tipo.id);
                      return (
                        <button
                          key={tipo.id}
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, tipoVehiculoId: String(tipo.id) }))}
                          style={{
                            textAlign: "left",
                            borderRadius: 12,
                            border: active ? "1px solid #4da3ff" : "1px solid rgba(255,255,255,0.12)",
                            background: active ? "rgba(39,119,255,0.18)" : "rgba(255,255,255,0.02)",
                            color: "white",
                            padding: "12px 14px",
                          }}
                        >
                          <div style={{ fontWeight: 700, marginBottom: 4 }}>{tipo.nombre}</div>
                          <div style={{ fontSize: 13, opacity: 0.9 }}>{tipo.descripcion || tipo.codigo}</div>
                        </button>
                      );
                    })}
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
                    <div style={{ fontSize: 12, opacity: 0.9 }}>Preview de posiciones</div>
                    <div style={{ fontSize: 12, opacity: 0.85 }}>{tipoActual?.cantidadCinturones ?? 0} cinturones</div>
                  </div>

                  <div
                    style={{
                      borderRadius: 14,
                      border: "1px solid rgba(255,255,255,0.08)",
                      background: "rgba(5,14,24,0.75)",
                      padding: 14,
                      display: "grid",
                      gap: 10,
                    }}
                  >
                    {previewRows.map((row, rowIndex) => (
                      <div
                        key={`row-${rowIndex}`}
                        style={{
                          display: "grid",
                          gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))`,
                          gap: 10,
                          justifyContent: "center",
                        }}
                      >
                        {row.map((posicion) => (
                          <span
                            key={posicion}
                            style={{
                              padding: "12px 10px",
                              borderRadius: 12,
                              background: rowIndex === 0 ? "rgba(47, 134, 255, 0.14)" : "rgba(255, 176, 32, 0.14)",
                              border: rowIndex === 0 ? "1px solid rgba(47, 134, 255, 0.24)" : "1px solid rgba(255, 176, 32, 0.24)",
                              fontSize: 12,
                              textAlign: "center",
                              minHeight: 48,
                              display: "grid",
                              placeItems: "center",
                            }}
                          >
                            {posicion}
                          </span>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </Form.Group>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="border-secondary">
        <Button variant="secondary" onClick={handleClose}>
          Cerrar
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={loading || !tipoActual}>
          {isEdit ? "Guardar cambios" : "Guardar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
